import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { StudentConectaTag } from '@/components/ui/custom/ConectaTag';
import { IconByStatus } from '@/components/ui/custom/dataDisplay/IconByUserStatus';
import NameAge from '@/components/ui/custom/dataDisplay/NameAge';
import Text from '@/components/ui/custom/dataDisplay/Text';
import TextWithCopy from '@/components/ui/custom/dataDisplay/TextWithCopy';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import ModalChangePassword from '@/components/ui/custom/modals/ModalChangePassword';
import { ModalRewards } from '@/components/ui/custom/modals/ModalRewards/ModalRewards';
import BodyCell from '@/components/ui/custom/table/BodyCell';
import KlassesDisplay from '@/components/ui/custom/table/KlassesDisplay';
import Row from '@/components/ui/custom/table/Row';
import UnitDisplay from '@/components/ui/custom/table/UnitDisplay';
import TableCell from '@/components/ui/custom/TableCell';
import {
  rewardsQueryKeys,
  userProfilesQueryKeys,
} from '@/data/services/querykeys';
import { ListUsersFilters } from '@/data/services/userServices';
import { Student } from '@/models/Student';
import { editFormAnimation } from '@/utils/animations/formAnimations';
import { StudentHeader } from '@/utils/HeaderTypes';
import Observable from '@/utils/observers/ObserverPattern';
import useFilterParams from '@/utils/UseFilterParams';
import {
  BriefcaseIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CoinIcon, LoadingIcon } from '../../icons';
import EditStudent from './EditStudent';
import PedagogicalSheet from './PedagogicalSheet/PedagogicalSheet';
import StudentHistoric from './StudentHistoric';
import StudentVacationNotice from './StudentVacationNotice/StudentVacationNotice';

interface ListStudentsProps {
  students: Student[];
  titles: StudentHeader;
}

export default function DataStudentsTable(props: ListStudentsProps) {
  const { students, titles } = props;

  const observer = new Observable<number>();

  return (
    <tbody className="gap-y-2.5 flex flex-col">
      {students.map(student => (
        <RenderRow
          key={student.id}
          student={student}
          titles={titles}
          observer={observer}
        />
      ))}
    </tbody>
  );
}

interface RenderRowStudentsProps {
  student: Student;
  titles: StudentHeader;
  observer?: Observable<number>;
}

export type StudentTabs = 'info' | 'sheet' | 'historic' | 'vacation';

function RenderRow({ student, titles, observer }: RenderRowStudentsProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'manageStudent',
  });

  const { actionId } = useParams();

  const { filterParams } = useFilterParams<ListUsersFilters>();

  const idIn = filterParams.getAll('idIn') ?? undefined;

  const [viewInformations, setViewInformations] = useState(
    idIn.at(0) === student.id.toString(),
  );
  const [isVisibleModal, setIsVisibleModal] = useState<
    'password' | 'rewards'
  >();

  function renderLastLogin(date?: Date) {
    return !date ? t('dataUserTable.never') : moment(date).format('DD/MM/YYYY');
  }

  const [tab, setTab] = useState<StudentTabs>(
    (actionId as StudentTabs) || 'info',
  );

  const toogleViewInformations = (newTab?: StudentTabs) => {
    if (tab !== newTab) {
      if (newTab) {
        setTab(newTab);
      }
      setViewInformations(true);
      return;
    }
    if (!viewInformations) observer?.notifyAll(student?.id);
    setViewInformations(old => !old);
    if (tab) setTab(tab);
  };

  useEffect(() => {
    if (student.id) {
      observer?.subscribe({
        id: student.id,
        action: () => {
          setViewInformations(false);
        },
      });
    }
  }, [student.id, observer]);

  return (
    <Row testId="trTestFilter">
      <BodyCell className="p-0 w-full">
        <div
          className="flex items-center cursor-pointer gap-5"
          style={{ width: `${titles.name.size}%` }}
          data-testid={`student${student.id}`}
          onClick={() => toogleViewInformations(tab)}
        >
          <IconButton
            testId="studentInfoButton"
            className="transition ease-in-out duration-150"
            icon={
              <ChevronUpIconWithAnimation
                animate={{
                  rotate: !viewInformations ? 180 : 0,
                }}
                className="w-3 h-3"
              />
            }
          />
          <NameAge user={student} className={{ text: 'inline' }} />
          <StudentConectaTag studentType={student.studentType} />
        </div>

        <KlassesDisplay
          klasses={student.klasses ?? []}
          width={titles.klass?.size}
        />

        <div
          className="flex items-center"
          style={{ width: `${titles.email?.size}%` }}
        >
          <TextWithCopy
            format="rubik-400"
            className={{ text: 'self-center h-fit break-all' }}
            label={student.email || ''}
          />
        </div>

        <UnitDisplay width={titles?.unit?.size} unitsIds={student.unitsIds} />

        <div
          data-testid="lastLogin"
          className="flex items-center"
          style={{ width: `${titles.lastAccess?.size}%` }}
        >
          <Text
            format="rubik-400"
            className="h-fit"
            text={renderLastLogin(student.lastLogin)}
          />
        </div>

        <div
          className="flex items-center"
          style={{ width: `${titles.status.size}%` }}
        >
          <Tooltip text={t(`status.${student.status}`)}>
            <IconByStatus status={student.status} />
          </Tooltip>
        </div>

        <div
          className="flex items-center gap-x-2.5"
          style={{ width: `${titles.actions.size}%` }}
        >
          <Tooltip text={t('dataUserTable.infoTooltip')}>
            <IconButton
              onClick={() => toogleViewInformations('info')}
              icon={<InformationCircleIcon className="text-primary w-6 h-6" />}
            />
          </Tooltip>
          <Tooltip text={t('dataUserTable.passwordTooltip')}>
            <IconButton
              testId="password"
              onClick={() => setIsVisibleModal('password')}
              icon={<LockClosedIcon className="text-primary w-6 h-6" />}
            />
          </Tooltip>
          <Tooltip text={t('dataUserTable.rewardsTooltip')}>
            <IconButton
              testId={`rewards${student.id}`}
              onClick={() => setIsVisibleModal('rewards')}
              icon={<CoinIcon className="text-primary w-6 h-6" />}
            />
          </Tooltip>
          <Tooltip text={t('dataUserTable.pedagogicalSheetTooltip')}>
            <IconButton
              testId={`sheet${student.id}`}
              onClick={() => toogleViewInformations('sheet')}
              icon={<DocumentTextIcon className="text-primary w-6 h-6" />}
            />
          </Tooltip>
          <Tooltip text={t('dataUserTable.historicTooltip')}>
            <IconButton
              testId={`historic${student.id}`}
              onClick={() => toogleViewInformations('historic')}
              icon={<FolderOpenIcon className="text-primary w-6 h-6" />}
            />
          </Tooltip>

          <Tooltip text={t('dataUserTable.vacationNoticeTooltip')}>
            <IconButton
              testId={`historic${student.id}`}
              onClick={() => toogleViewInformations('vacation')}
              icon={<BriefcaseIcon className="text-primary w-6 h-6" />}
            />
          </Tooltip>
        </div>
      </BodyCell>

      <TableCell>
        <ModalChangePassword
          user={student}
          visible={isVisibleModal === 'password'}
          onClickCancel={() => setIsVisibleModal(undefined)}
        />
        <ModalRewards
          student={student}
          isVisible={isVisibleModal === 'rewards'}
          onClickConfirm={() => setIsVisibleModal(undefined)}
          onClickCancel={() => setIsVisibleModal(undefined)}
        />
      </TableCell>
      <BodyCell>
        <AnimatePresence>
          {viewInformations && (
            <motion.div className="w-full" {...editFormAnimation}>
              <StudentDetails student={student} tab={tab} />
            </motion.div>
          )}
        </AnimatePresence>
      </BodyCell>
    </Row>
  );
}

interface StudentDetailsProps {
  student: Student;
  tab: StudentTabs;
  onUpdate?: () => void;
}

function StudentDetails({ student, tab }: StudentDetailsProps) {
  const { data: rewards, isInitialLoading: isLoadingRewards } = useQuery({
    ...rewardsQueryKeys.get(student.id),
    enabled: !!student.id && tab === 'info',
  });

  const { data: profile, isInitialLoading: isLoadingProfile } = useQuery({
    ...userProfilesQueryKeys.get(student.username),
    enabled: !!student.username && tab === 'info',
  });

  const tabs = {
    info: (
      <ConditionalRenderer condition={!!profile}>
        {profile && (
          <EditStudent
            user={student}
            profile={profile}
            rewards={rewards}
            isLoadingRewards={isLoadingRewards}
          />
        )}
      </ConditionalRenderer>
    ),
    sheet: <PedagogicalSheet student={student} />,
    historic: <StudentHistoric student={student} />,
    vacation: <StudentVacationNotice student={student} />,
  };

  return (
    <div className="flex w-full">
      <ConditionalRenderer
        condition={!isLoadingProfile}
        fallback={
          <div className="flex w-full justify-center p-3.5">
            <LoadingIcon className="text-primary/40 w-10" />
          </div>
        }
      >
        {tabs[tab || 'info']}
      </ConditionalRenderer>
    </div>
  );
}

const ChevronUpIconWithAnimation = motion.create(ChevronUpIcon);
